/**
 * Position Calculator - Pure functions for sticky state and position calculations
 *
 * These functions have no side effects and are fully testable.
 * They determine the sticky state and calculate the appropriate CSS values.
 */

import { STICKY_STATE } from './config.js';

/**
 * Calculate the scroll thresholds where state transitions occur
 * @param {Object} cache - Cached measurements
 * @returns {Object} - { stickyStart, stickyEnd }
 */
export function calculateScrollThresholds(cache) {
	if (!cache) {
		return { stickyStart: 0, stickyEnd: 0 };
	}

	const STICKY_OFFSET = 16; // Additional spacing below header when sticky

	// Compute absolute stickyElementTop from container position + relative offset
	// This works correctly even when other sections load and push content down,
	// because the offset (relative position within container) doesn't change
	const stickyElementTop = cache.containerTop + cache.stickyOffsetFromContainer;

	// Sticky starts when scroll would push sidebar behind header
	const stickyStart = stickyElementTop - (cache.navHeight + STICKY_OFFSET);

	// Sticky ends when bottom of sticky element would pass stop marker
	const stickyEnd = cache.stopElementTop
		? cache.stopElementTop - (cache.navHeight + STICKY_OFFSET) - cache.stickyHeight
		: Infinity;

	return { stickyStart, stickyEnd };
}

/**
 * Determine the current sticky state based on scroll position
 * @param {number} scrollY - Current scroll position
 * @param {Object} thresholds - { stickyStart, stickyEnd }
 * @returns {string} - One of STICKY_STATE values
 */
export function determineState(scrollY, thresholds) {
	if (scrollY < thresholds.stickyStart) {
		return STICKY_STATE.STATIC;
	}

	if (thresholds.stickyEnd !== Infinity && scrollY >= thresholds.stickyEnd) {
		return STICKY_STATE.STOPPED;
	}

	return STICKY_STATE.STICKY;
}

/**
 * Get CSS position styles for the given state
 * @param {string} state - Current STICKY_STATE
 * @param {Object} cache - Cached measurements
 * @param {boolean} hasStopMarker - Whether explicit stop marker exists
 * @returns {Object} - { position, top } CSS values
 */
export function getPositionStyles(state, cache, hasStopMarker) {
	if (!cache) {
		return { position: '', top: '' };
	}

	switch (state) {
		case STICKY_STATE.STATIC:
			// Position absolute to override default CSS fixed positioning
			// But don't set top value - let element stay at natural layout position
			return {
				position: 'absolute',
				top: ''
			};

		case STICKY_STATE.STICKY:
			// Fixed to viewport with 16px spacing below header
			return {
				position: 'fixed',
				top: `${cache.navHeight + 16}px`
			};

		case STICKY_STATE.STOPPED:
			// Absolute position pinned at stop marker
			const spacing = hasStopMarker ? 0 : 60;
			const absoluteTop = cache.stopElementTop - cache.mainContentTop - cache.stickyHeight - spacing;
			return {
				position: 'absolute',
				top: `${absoluteTop}px`
			};

		default:
			return { position: '', top: '' };
	}
}
